import { useState } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CalendarDays, Plus, Send, Trash2, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { JobStatusBadge, ProposalStatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import { useAcceptProposal, useCloseJob, useJob, useSubmitProposal } from '@/features/marketplace/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatRelative, formatRupiah } from '@/lib/format'
import type { Proposal } from '@/lib/types'

const route = getRouteApi('/public/proyek/$jobId')

export function JobDetailPage() {
  const { jobId } = route.useParams()
  const { data: job, isLoading } = useJob(jobId)
  const { data: session } = useSession()
  const [proposalOpen, setProposalOpen] = useState(false)
  const [acceptTarget, setAcceptTarget] = useState<Proposal | null>(null)
  const closeJob = useCloseJob(jobId)
  const toast = useToast()

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    )
  }

  if (!job) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Proyek tidak ditemukan"
          description="Proyek mungkin sudah ditutup atau dihapus pemiliknya."
          action={
            <Link to="/proyek">
              <Button variant="outline">Kembali ke papan proyek</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const isOwner = session?.user.id === job.client.id
  const isExpert = session?.user.roles.includes('expert') ?? false
  const canPropose = isExpert && !isOwner && job.status === 'open'

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <Link
        to="/proyek"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Papan proyek
      </Link>

      <div className="mt-4 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-2">
                <JobStatusBadge status={job.status} />
                <Badge tone="brand">{job.category}</Badge>
                <span className="text-xs text-subtle">Diposting {formatRelative(job.created_at)}</span>
              </div>

              <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">{job.title}</h1>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {job.skills.map((skill) => (
                  <span key={skill} className="rounded-full bg-sunken px-3 py-1 text-sm font-medium text-muted">
                    {skill}
                  </span>
                ))}
              </div>

              <p className="mt-5 whitespace-pre-line leading-relaxed text-muted">{job.description}</p>
            </CardBody>
          </Card>

          {isOwner ? (
            <Card>
              <CardHeader
                title={`Proposal masuk (${job.proposals?.length ?? 0})`}
                description="Bandingkan penawaran, lalu terima satu untuk menerbitkan kontrak."
              />

              {!job.proposals || job.proposals.length === 0 ? (
                <EmptyState
                  icon={<Users className="size-6" />}
                  title="Belum ada proposal"
                  description="Proposal dari tenaga ahli akan muncul di sini beserta rincian penawarannya."
                />
              ) : (
                <ul className="divide-y divide-[var(--border)]">
                  {job.proposals.map((proposal) => (
                    <li key={proposal.id} className="px-5 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-bold text-ink">{proposal.expert.full_name}</p>
                            <ProposalStatusBadge status={proposal.status} />
                          </div>
                          {proposal.headline ? (
                            <p className="mt-0.5 text-sm text-muted">{proposal.headline}</p>
                          ) : null}
                          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted">
                            {proposal.cover_letter}
                          </p>
                        </div>

                        <div className="shrink-0 text-right">
                          <p className="text-lg font-extrabold tabular-nums text-ink">
                            {formatRupiah(proposal.amount)}
                          </p>
                          <p className="text-xs text-subtle">{proposal.duration_days} hari pengerjaan</p>
                          {job.status === 'open' && proposal.status === 'submitted' ? (
                            <Button size="sm" className="mt-3" onClick={() => setAcceptTarget(proposal)}>
                              Terima proposal
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ) : null}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:h-fit">
          <Card>
            <CardBody className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-subtle">Anggaran</p>
                <p className="mt-1 text-xl font-extrabold text-ink">
                  {formatRupiah(job.budget_min)} – {formatRupiah(job.budget_max)}
                </p>
              </div>

              <dl className="space-y-2 border-t border-line pt-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Klien</dt>
                  <dd className="font-semibold text-ink">{job.client.full_name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Proposal masuk</dt>
                  <dd className="font-semibold text-ink">{job.proposal_count}</dd>
                </div>
                {job.duration_days ? (
                  <div className="flex justify-between">
                    <dt className="text-muted">Perkiraan durasi</dt>
                    <dd className="font-semibold text-ink">{job.duration_days} hari</dd>
                  </div>
                ) : null}
                {job.deadline_at ? (
                  <div className="flex justify-between">
                    <dt className="text-muted">Tenggat</dt>
                    <dd className="font-semibold text-ink">{formatDate(job.deadline_at)}</dd>
                  </div>
                ) : null}
              </dl>

              {canPropose ? (
                <Button fullWidth size="lg" onClick={() => setProposalOpen(true)}>
                  <Send className="size-4" />
                  Kirim proposal
                </Button>
              ) : null}

              {isOwner && job.status === 'open' ? (
                <Button
                  fullWidth
                  variant="outline"
                  loading={closeJob.isPending}
                  onClick={() => {
                    if (!window.confirm('Tutup proyek ini? Proposal baru tidak lagi diterima.')) return
                    closeJob.mutate(undefined, {
                      onSuccess: () => toast.info('Proyek ditutup'),
                      onError: () => toast.error('Gagal menutup proyek'),
                    })
                  }}
                >
                  Tutup proyek
                </Button>
              ) : null}

              {!session ? (
                <Link to="/masuk" search={{ redirect: `/proyek/${jobId}` }}>
                  <Button fullWidth variant="secondary">
                    Masuk untuk mengirim proposal
                  </Button>
                </Link>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-2 text-sm text-muted">
              <p className="font-bold text-ink">Bagaimana pembayarannya?</p>
              <p>
                Setelah proposal diterima, klien menyetor dana ke rekening penampungan. Pekerjaan baru dimulai
                ketika dana benar-benar terkunci.
              </p>
              <p>Dana dilepas per milestone begitu hasil kerja disetujui.</p>
            </CardBody>
          </Card>
        </aside>
      </div>

      <ProposalDialog jobId={jobId} open={proposalOpen} onClose={() => setProposalOpen(false)} />
      <AcceptDialog proposal={acceptTarget} onClose={() => setAcceptTarget(null)} />
    </div>
  )
}

function ProposalDialog({ jobId, open, onClose }: { jobId: string; open: boolean; onClose: () => void }) {
  const submit = useSubmitProposal(jobId)
  const toast = useToast()
  const [values, setValues] = useState({ cover_letter: '', amount: 0, duration_days: 14 })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kirim proposal"
      description="Jelaskan rencana pengerjaan Anda sekonkret mungkin."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={submit.isPending}
            disabled={values.cover_letter.trim().length < 30 || values.amount <= 0}
            onClick={() =>
              submit.mutate(values, {
                onSuccess: () => {
                  toast.success('Proposal terkirim', 'Klien akan meninjau penawaran Anda.')
                  onClose()
                },
                onError: (error) =>
                  toast.error('Gagal mengirim proposal', error instanceof ApiError ? error.message : undefined),
              })
            }
          >
            Kirim proposal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Rencana pengerjaan"
          rows={6}
          value={values.cover_letter}
          onChange={(event) => setValues((current) => ({ ...current, cover_letter: event.target.value }))}
          placeholder="Pengalaman relevan, tahapan pengerjaan, dan apa yang Anda butuhkan dari klien."
          hint="Minimal 30 karakter. Proposal yang menyebut tahapan konkret lebih sering dipilih."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nilai penawaran"
            type="number"
            min={0}
            step={100000}
            prefix="Rp"
            value={values.amount || ''}
            onChange={(event) => setValues((current) => ({ ...current, amount: Number(event.target.value) }))}
          />
          <Input
            label="Durasi (hari)"
            type="number"
            min={1}
            value={values.duration_days}
            onChange={(event) =>
              setValues((current) => ({ ...current, duration_days: Number(event.target.value) }))
            }
          />
        </div>
      </div>
    </Dialog>
  )
}

interface PlanRow {
  title: string
  amount: number
}

/**
 * Penerimaan proposal sekaligus menyusun milestone.
 *
 * Total milestone wajib sama persis dengan nilai kontrak — aturan yang juga
 * ditegakkan basis data. Selisihnya ditampilkan langsung agar klien tidak
 * perlu menghitung sendiri.
 */
function AcceptDialog({ proposal, onClose }: { proposal: Proposal | null; onClose: () => void }) {
  const accept = useAcceptProposal()
  const navigate = useNavigate()
  const toast = useToast()
  const [rows, setRows] = useState<PlanRow[]>([])

  const total = proposal?.amount ?? 0
  const allocated = rows.reduce((sum, row) => sum + (Number.isFinite(row.amount) ? row.amount : 0), 0)
  const remaining = total - allocated
  const balanced = rows.length === 0 || remaining === 0

  const reset = () => {
    setRows([])
    onClose()
  }

  return (
    <Dialog
      open={proposal !== null}
      onClose={reset}
      title="Terima proposal"
      description={proposal ? `${proposal.expert.full_name} · ${formatRupiah(proposal.amount)}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={reset}>
            Batal
          </Button>
          <Button
            loading={accept.isPending}
            disabled={!balanced}
            onClick={() => {
              if (!proposal) return
              accept.mutate(
                {
                  proposalId: proposal.id,
                  milestones: rows.map((row) => ({ title: row.title, amount: row.amount })),
                },
                {
                  onSuccess: (data) => {
                    toast.success('Kontrak dibuat', 'Lanjutkan dengan menyetor dana escrow.')
                    reset()
                    navigate({ to: '/app/kontrak/$contractId', params: { contractId: data.contract.id } })
                  },
                  onError: (error) =>
                    toast.error('Gagal menerima proposal', error instanceof ApiError ? error.message : undefined),
                },
              )
            }}
          >
            Terima &amp; buat kontrak
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Bagi pekerjaan menjadi beberapa tahap agar dana dilepas bertahap. Biarkan kosong untuk membuat satu
          milestone senilai penuh.
        </p>

        {rows.map((row, index) => (
          <div key={index} className="flex items-end gap-2">
            <Input
              label={`Milestone ${index + 1}`}
              value={row.title}
              onChange={(event) =>
                setRows((current) =>
                  current.map((item, position) =>
                    position === index ? { ...item, title: event.target.value } : item,
                  ),
                )
              }
              placeholder="Nama tahap pekerjaan"
              className="flex-1"
            />
            <Input
              label="Nilai"
              type="number"
              min={0}
              step={100000}
              prefix="Rp"
              value={row.amount || ''}
              onChange={(event) =>
                setRows((current) =>
                  current.map((item, position) =>
                    position === index ? { ...item, amount: Number(event.target.value) } : item,
                  ),
                )
              }
              className="w-44"
            />
            <Button
              size="icon"
              variant="ghost"
              className="mb-0.5 text-danger"
              aria-label={`Hapus milestone ${index + 1}`}
              onClick={() => setRows((current) => current.filter((_, position) => position !== index))}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}

        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setRows((current) => [
              ...current,
              { title: `Tahap ${current.length + 1}`, amount: current.length === 0 ? total : 0 },
            ])
          }
        >
          <Plus className="size-4" />
          Tambah milestone
        </Button>

        {rows.length > 0 ? (
          <div className={`rounded-xl p-4 ${balanced ? 'bg-success-soft' : 'bg-warning-soft'}`}>
            <div className="flex justify-between text-sm">
              <span className="text-muted">Total milestone</span>
              <span className="font-bold text-ink">{formatRupiah(allocated)}</span>
            </div>
            <div className="mt-1 flex justify-between text-sm">
              <span className="text-muted">Nilai kontrak</span>
              <span className="font-bold text-ink">{formatRupiah(total)}</span>
            </div>
            {!balanced ? (
              <p className="mt-2 text-sm font-semibold text-warning">
                {remaining > 0
                  ? `Masih kurang ${formatRupiah(remaining)} agar sama dengan nilai kontrak.`
                  : `Kelebihan ${formatRupiah(Math.abs(remaining))} dari nilai kontrak.`}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-start gap-3 rounded-xl bg-sunken p-4 text-sm text-muted">
          <CalendarDays className="mt-0.5 size-4 shrink-0" />
          <p>
            Setelah kontrak terbit, Anda perlu menyetor dana escrow sebelum tenaga ahli mulai bekerja.
            Milestone pertama otomatis berjalan begitu dana masuk.
          </p>
        </div>
      </div>
    </Dialog>
  )
}
